import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Grid,
  Link,
  Skeleton,
  Typography,
} from '@mui/material';
import { find, isEmpty, map } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { getLatestJobByFilters } from 'api/jobs';
import { IngestJob, JobType, TileGenerationJob } from 'interfaces/job';
import { useJobIngestDetails } from 'utils/queryHooks/jobs/useJobIngestDetails';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';
import { formatOverallStatus, getOverallStatusColor } from './IngestJobDetailsSection';
import { JobDetailCard, JobDetailSection, JobDetailValue } from './JobDetailCard';
import { JobDetailsLink } from './JobDetailsLink';

const latestIngestJobForTileGenerationQueryKey = 'latest-ingest-job-for-tile-generation';

const getTileGenerationSlideId = (job: Partial<TileGenerationJob>) => {
  const slideId = job.params?.slide_id;
  return typeof slideId === 'string' && !isEmpty(slideId) ? slideId : undefined;
};

export const TileGenerationJobDetailsSection: React.FC<{
  job: Partial<TileGenerationJob>;
}> = ({ job }) => {
  const { labId } = useCurrentLabId();
  const { getUrlToSlidePage } = useNavigationToViewerPage();
  const slideId = getTileGenerationSlideId(job);
  const canLookupIngestJob = !isEmpty(slideId) && !isEmpty(labId);

  const { data: latestIngestJob, isLoading: isLoadingLatestIngestJob } =
    useQueryWithErrorAndRetrySnackbar<IngestJob | null>(
      [latestIngestJobForTileGenerationQueryKey, labId, job.studyId, slideId],
      ({ signal }) =>
        getLatestJobByFilters<IngestJob>(
          {
            filters: {
              studyId: job.studyId || '',
              types: [JobType.Ingest],
              slideIds: slideId ? [slideId] : [],
            },
            labId,
          },
          signal
        ),
      {
        enabled: canLookupIngestJob,
        retry: false,
        queriedEntityName: 'matching ingest job',
        consoleOnly: true,
      }
    );

  const { data: ingestDetails, isLoading: isLoadingIngestDetails } = useJobIngestDetails(latestIngestJob?.id || '', {
    enabled: Boolean(latestIngestJob?.id),
  });
  const isLoading = isLoadingLatestIngestJob || isLoadingIngestDetails;
  const resolvedLabId = ingestDetails?.labId || labId;
  const matchedSlide = find(ingestDetails?.slides, (slide) => slide.slideId === slideId);
  const sourceFileName = matchedSlide?.originalFileName;
  const slideUrl = slideId && resolvedLabId ? getUrlToSlidePage({ slideId, labId: resolvedLabId }) : undefined;

  return (
    <Grid item>
      <Accordion defaultExpanded slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="tile-status-content" id="tile-status-header">
          <Grid container alignItems="center" spacing={1} wrap="nowrap">
            <Grid item xs>
              <Typography variant="h4">Tile Generation Details</Typography>
            </Grid>
            {ingestDetails && (
              <Grid item>
                <Chip
                  label={formatOverallStatus(ingestDetails.overallStatus)}
                  color={getOverallStatusColor(ingestDetails.overallStatus)}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <JobDetailCard>
                <JobDetailSection label="Processed slide details">
                  <Grid container direction="column">
                    {slideId && slideUrl ? (
                      <Link
                        to={slideUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        component={RouterLink}
                        underline="hover"
                      >
                        {slideId}
                      </Link>
                    ) : (
                      <Typography variant="body1">{slideId || '---'}</Typography>
                    )}
                    {matchedSlide?.slideFormat && (
                      <Typography variant="caption" color="text.secondary">
                        {matchedSlide.slideFormat}
                      </Typography>
                    )}
                  </Grid>
                </JobDetailSection>

                {latestIngestJob && (
                  <JobDetailSection label="Matched ingest job">
                    <JobDetailValue>{latestIngestJob.name || latestIngestJob.id}</JobDetailValue>
                    <Typography variant="caption" color="text.secondary">
                      {latestIngestJob.id ? (
                        <JobDetailsLink jobId={latestIngestJob.id}>{latestIngestJob.id}</JobDetailsLink>
                      ) : null}
                      {latestIngestJob.id && latestIngestJob.status ? ' · ' : null}
                      {latestIngestJob.status}
                    </Typography>
                  </JobDetailSection>
                )}

                {isLoading && (
                  <Grid item>
                    <Skeleton variant="rectangular" height={70} />
                  </Grid>
                )}

                {sourceFileName && (
                  <JobDetailSection label="Source file">
                    <JobDetailValue variant="h5">{sourceFileName || '---'}</JobDetailValue>
                  </JobDetailSection>
                )}

                {!isLoading && slideId && !latestIngestJob && (
                  <Grid item>
                    <Alert severity="warning">No matching ingest job was found for this slide.</Alert>
                  </Grid>
                )}

                {!isLoading && !slideId && (
                  <Grid item>
                    <Alert severity="warning">No slide ID recorded on this tile generation job.</Alert>
                  </Grid>
                )}

                {!isLoading && ingestDetails && !isEmpty(ingestDetails.warnings) && (
                  <Grid item>
                    {map(ingestDetails.warnings, (warning) => (
                      <Alert severity="warning" key={warning}>
                        {warning}
                      </Alert>
                    ))}
                  </Grid>
                )}
              </JobDetailCard>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
